import { QuizQuestion } from '../../api/quiz-services';

export const assessmentMetadata = {
    title: "Module 9 Quiz",
    duration: 25
};

export const module9Questions: QuizQuestion[] = [
    {
        id: "M9-001",
        moduleId: "module-9",
        category: "Vision",
        text: "To qualify for a standard, unrestricted driver license in Ohio, what is the minimum visual acuity standard required in at least one or both eyes?",
        options: ["20/20 vision", "20/40 vision", "20/60 vision", "20/100 vision"],
        correctAnswer: 1,
        scenario: "You are applying for a standard, unrestricted Ohio driver's license. What is the minimum visual acuity standard?",
        explanation: "To qualify for an unrestricted driver's license in Ohio, you must have a minimum visual acuity of 20/40 in at least one or both eyes, with or without corrective lenses."
    },
    {
        id: "M9-002",
        moduleId: "module-9",
        category: "Auditory",
        text: "Under Ohio law (ORC 4511.84), what is the regulation regarding wearing headphones or earplugs while operating a motor vehicle?",
        options: [
            "It is legal only when using a navigation system",
            "It is illegal to wear them in both ears, and at least one ear must remain completely uncovered",
            "It is fully legal at all times on municipal roadways",
            "It is legal for drivers over the age of 21 only"
        ],
        correctAnswer: 1,
        scenario: "A passenger suggests you wear noise-cancelling headphones in both ears while driving to listen to navigation prompts.",
        explanation: "Ohio Revised Code strictly prohibits wearing headphones or earplugs in *both* ears while operating a vehicle, as this blocks sirens, train whistles, and safety warnings."
    },
    {
        id: "M9-003",
        moduleId: "module-9",
        category: "Kinesthesia",
        text: "Which sensory system provides the 'seat-of-the-pants' physical feedback that allows a driver to detect vehicle weight shifts, steering resistance drops, and traction loss?",
        options: [
            "Vestibular sound vibration detection",
            "Kinesthesia (tactile and balance movement perception)",
            "Acoustic diagnostic frequency scanning",
            "Fringe visual depth calculation"
        ],
        correctAnswer: 1,
        scenario: "You feel your vehicle's rear suspension begin to slide laterally before you visually detect any path change. What sense is this?",
        explanation: "Kinesthesia is the sense that perceives physical motion, balance, and G-force shifts. It provides immediate, physical feedback about tire grip and vehicle stability."
    },
    {
        id: "M9-004",
        moduleId: "module-9",
        category: "Psychological",
        text: "Under intense emotional stress or extreme excitement, a driver can lose up to what percentage of their cognitive hazard-processing capacity?",
        options: ["10%", "20%", "50%", "80%"],
        correctAnswer: 2,
        scenario: "You get into an intense argument before driving. A passenger notes that you seem highly distracted. How does this affect your brain?",
        explanation: "Under intense emotional stress, anger, or excitement, your brain's cognitive hazard-processing capacity can drop by up to 50%, severely slowing your reaction times."
    },
    {
        id: "M9-005",
        moduleId: "module-9",
        category: "Psychological",
        text: "Which deep breathing sequence is recommended to naturally lower your heart rate and recover from an emotional hijack?",
        options: [
            "Inhale 1 count, hold 3, exhale 1, hold 4",
            "Inhale 3 counts, hold 1, exhale 4, hold 1",
            "Inhale 5 counts, hold 5, exhale 5, hold 5",
            "Inhale 4 counts, hold 7, exhale 8, hold 0"
        ],
        correctAnswer: 1,
        scenario: "You feel extremely angry and noticed your heart rate spike after a near-miss. What breathing technique helps reset your nervous system?",
        explanation: "Practicing deep diaphragmatic breathing—such as the 3-second inhale, 1-second hold, 4-second exhale, 1-second hold pattern—activates the vagus nerve to reduce heart rate."
    },
    {
        id: "M9-006",
        moduleId: "module-9",
        category: "Psychological",
        text: "If an enraged driver is actively following or targeting you in a road rage incident, what is the safest course of action?",
        options: [
            "Pull over on a dark shoulder and exit your vehicle to confront them",
            "Drive directly to your home and park in your driveway",
            "Keep doors locked and windows up, avoid eye contact, and drive to a public police or fire station",
            "Accelerate to high speed and weave through traffic to escape them"
        ],
        correctAnswer: 2,
        scenario: "An enraged driver is tailgating you, honking, and gesturing aggressively. What is your safest defensive escape course?",
        explanation: "Keep windows up and doors locked. Avoid eye contact or retaliatory gestures, and drive directly to a public, populated police or fire station. Never drive home!"
    },
    {
        id: "M9-007",
        moduleId: "module-9",
        category: "Fatigue",
        text: "Being awake for 24 hours straight is physiologically equivalent to which blood alcohol concentration (BAC) level?",
        options: [".02% BAC", ".05% BAC", ".08% BAC", ".10% BAC"],
        correctAnswer: 3,
        scenario: "You have been awake for 24 hours straight and decide to drive home. How does this fatigue affect your body?",
        explanation: "Being awake for 24 hours straight causes cognitive and motor skill degradation equivalent to a Blood Alcohol Concentration (BAC) of 0.10%, which is well over the legal limit."
    },
    {
        id: "M9-008",
        moduleId: "module-9",
        category: "Fatigue",
        text: "To prevent the trance-like state of highway hypnosis during a long trip, Ohio guidelines recommend a driver take regular breaks at what minimum intervals?",
        options: [
            "Every 50 miles or 1 hour",
            "Every 100 miles or 2 hours",
            "Every 200 miles or 4 hours",
            "Only when you start yawning"
        ],
        correctAnswer: 1,
        scenario: "You are planning a long 8-hour interstate road trip. How often should you plan rest breaks to prevent highway hypnosis?",
        explanation: "To prevent the trance-like state of highway hypnosis, safety guidelines mandate taking a 10-to-15 minute rest break every 100 miles or every 2 hours of continuous driving."
    },
    {
        id: "M9-009",
        moduleId: "module-9",
        category: "Fatigue",
        text: "At standard highway speeds of 65 MPH, a brief 5-second microsleep will result in the vehicle traveling approximately how far while the driver is completely unconscious?",
        options: ["100 feet", "250 feet", "500 feet", "1,000 feet"],
        correctAnswer: 2,
        scenario: "You feel extremely drowsy at standard highway speeds of 65 mph. If you experience a brief 5-second microsleep, how far will the car travel?",
        explanation: "At 65 mph, your vehicle travels approximately 95 feet per second. A brief 5-second microsleep will result in your vehicle traveling nearly 500 feet (1.5 football fields) while you are completely unconscious."
    },
    {
        id: "M9-010",
        moduleId: "module-9",
        category: "Illness",
        text: "How much can a common cold or minor short-term illness slow a driver's reaction time and degrade their driving safety?",
        options: ["5%", "10% to 15%", "25%", "50%"],
        correctAnswer: 1,
        scenario: "You have a common head cold and runny nose. A classmate suggests that minor illnesses have no real impact on your driving skills.",
        explanation: "Even minor short-term illnesses (like a cold or flu) can slow your physical reaction time and hazard detection by 10% to 15%, requiring you to expand your following distance."
    },
    {
        id: "M9-011",
        moduleId: "module-9",
        category: "Medication",
        text: "On a legal over-the-counter (OTC) medication label, what does the warning 'do not operate heavy machinery' explicitly mean for a driver?",
        options: [
            "It only applies to commercial trucks, tractors, and forklifts",
            "It means you should not operate any motor vehicle, including a standard passenger car",
            "It only applies when driving during night conditions",
            "It is a general suggestion and has no legal or OVI safety binding"
        ],
        correctAnswer: 1,
        scenario: "You purchase an over-the-counter allergy medicine. The label warns 'May cause drowsiness; use caution when operating machinery.' What is the rule?",
        explanation: "A standard passenger car is classified as heavy machinery. If a medication warning indicates it may cause drowsiness, you must not drive, as you can be charged with OVI."
    },
    {
        id: "M9-012",
        moduleId: "module-9",
        category: "Medication",
        text: "Under the 'synergistic effect', what occurs when a driver mixes alcohol with over-the-counter or prescription medications?",
        options: [
            "The substances cancel each other out, restoring reaction time",
            "The combined impairment effect is multiplied exponentially, making it significantly greater than the sum of their individual effects",
            "The body metabolizes both substances twice as fast",
            "The drowsiness effect is eliminated while focus is sharpened"
        ],
        correctAnswer: 1,
        scenario: "Under Ohio law, teen drivers face strict accountability. If you are **under the age of 18** and receive **two moving violations**, your driver's license will be suspended automatically for **90 days**. If you receive a **third moving violation**, the consequences escalate to a mandatory **one-year license suspension**.",
        explanation: ""
    },
    {
        id: "M9-013",
        moduleId: "module-9",
        category: "Adaptive Technology",
        text: "In a vehicle equipped with certified manual hand controls for physical disabilities, how are the accelerator and brake controls operated?",
        options: [
            "Pushing the hand control lever forward applies the accelerator, while pulling it backward applies the brakes",
            "Pushing the hand control lever forward applies the brakes, while pulling it backward/downward applies the accelerator",
            "Speed is controlled entirely by steering wheel button inputs",
            "Speed is managed by voice activation in modern certified vehicles"
        ],
        correctAnswer: 1,
        scenario: "You are inspecting a vehicle equipped with certified manual hand controls for physical disabilities. How are they physically operated?",
        explanation: "Certified manual hand controls use a push-pull mechanical leverage: pushing the lever forward applies the service brakes, and pulling it backward or down applies the accelerator."
    },
    {
        id: "M9-014",
        moduleId: "module-9",
        category: "Compliance",
        text: "Under Ohio BMV regulations, what is the legal consequence of operating a vehicle without the required adaptive equipment listed as a restriction on your license?",
        options: [
            "It is permitted during daylight hours or in residential areas",
            "It is a serious violation equivalent to driving without a valid license, carrying high fines and potential license suspension",
            "It is considered a minor mechanical warning with no legal penalty",
            "It is fully legal if you are driving a registered passenger vehicle"
        ],
        correctAnswer: 1,
        scenario: "Your driver's license carries a restriction code requiring adaptive equipment. What is the legal penalty for driving a standard car without it?",
        explanation: "Operating a vehicle without the required adaptive equipment listed on your license restriction is a major violation equivalent to driving without a license."
    },
    {
        id: "M9-015",
        moduleId: "module-9",
        category: "Carbon Monoxide",
        text: "Why is carbon monoxide (CO) extremely dangerous for vehicle occupants?",
        options: [
            "It has a distinct sweet odor that induces sleep, but has no physical effects on blood oxygen",
            "It is invisible, odorless, and tasteless, and it bonds 200 times stronger to red blood cells than oxygen, causing cellular suffocation",
            "It only builds up in vehicles with open windows and has a bright green chemical color",
            "It acts as a stimulant that keeps drivers awake but slows steering response times"
        ],
        correctAnswer: 1,
        scenario: "A passenger asks you why carbon monoxide (CO) gas is extremely lethal compared to other automotive exhaust gases.",
        explanation: "Carbon monoxide is invisible, odorless, and tasteless. It bonds 200 times stronger to hemoglobin than oxygen, preventing red blood cells from transporting oxygen, causing cellular suffocation."
    },
    {
        id: "M9-016",
        moduleId: "module-9",
        category: "Carbon Monoxide",
        text: "During winter driving conditions in Ohio, why is running your vehicle's engine while backed into a snowbank or deep snow especially hazardous?",
        options: [
            "It will cause the vehicle's electrical heating system to short circuit immediately",
            "The blocked tailpipe forces lethal carbon monoxide gas under the chassis and directly into the passenger cabin",
            "It increases tire pressure to dangerous levels, leading to high-speed blowouts",
            "The cold temperature increases fuel consumption by 300% but does not impact exhaust flow"
        ],
        correctAnswer: 1,
        scenario: "Your car becomes stuck in a deep snowbank. Why is it dangerous to run the engine while waiting for help without checking the tailpipe?",
        explanation: "If the tailpipe is blocked by snow or mud, exhaust gas cannot escape. This forces lethal carbon monoxide under the chassis and directly into the passenger cabin, causing rapid poisoning."
    },
    {
        id: "M9-017",
        moduleId: "module-9",
        category: "Alcohol",
        text: "In terms of pure alcohol content, which of the following standard drinks contains the same amount of alcohol?",
        options: [
            "A 12oz beer has significantly more alcohol than a 5oz glass of wine",
            "A 12oz of 5% beer, 5oz of 12% wine, and 1.5oz of 80-proof distilled spirits all contain the exact same amount of pure alcohol",
            "A shot of distilled spirits is always twice as intoxicating as a glass of wine",
            "Beer is metabolized by the liver twice as fast as distilled spirits"
        ],
        correctAnswer: 1,
        scenario: "A friend claims that drinking a standard glass of wine is safer than drinking a shot of 80-proof whiskey before driving. Is this true?",
        explanation: "A standard 12oz beer (5%), a 5oz glass of wine (12%), and a 1.5oz shot of 80-proof spirits all contain exactly the same amount of pure alcohol (approximately 0.6 ounces)."
    },
    {
        id: "M9-018",
        moduleId: "module-9",
        category: "OVI Laws",
        text: "Under Ohio law (OVUAC), what is the strict Blood Alcohol Concentration (BAC) legal limit for drivers under the age of 21?",
        options: [
            "0.00% BAC (absolute zero)",
            "0.02% BAC, which allows for trace amounts in OTC medications while enforcing zero-tolerance for underage drinking",
            "0.05% BAC, matching standard European guidelines",
            "0.08% BAC, identical to the adult legal limit"
        ],
        correctAnswer: 1,
        scenario: "You are under the age of 21. What is the Blood Alcohol Concentration (BAC) legal limit that will trigger an OVI arrest in Ohio?",
        explanation: "Ohio enforces a strict 'Zero Tolerance' law for drivers under 21. Any BAC of 0.02% or higher will result in an OVI arrest and immediate administrative license suspension."
    },
    {
        id: "M9-019",
        moduleId: "module-9",
        category: "Distracted Driving",
        text: "Under Ohio’s statewide primary offense electronic device law (HB 33), which of the following is true regarding cell phone use while driving?",
        options: [
            "It is legal for all drivers to text as long as they are stopped at a traffic light",
            "Law enforcement can pull you over and issue a ticket simply for holding an electronic wireless device in your hand while driving",
            "Drivers under the age of 18 are permitted to use hands-free Bluetooth devices for non-emergency calls",
            "Navigation apps can be actively programmed and holding the phone is permitted while the vehicle is in motion"
        ],
        correctAnswer: 1,
        scenario: "You are stopped at a red light and hold your phone to read a text message. Can an officer pull you over simply for holding the phone?",
        explanation: "Under Ohio's primary offense distracted driving law (HB 33), it is illegal to hold or support an electronic wireless device in your hand while driving. Officers can pull you over for this."
    },
    {
        id: "M9-020",
        moduleId: "module-9",
        category: "Distracted Driving",
        text: "According to cognitive research and Ohio driver safety guidelines, how does using a cell phone or electronic device impact a driver's brain activity and performance?",
        options: [
            "It increases situational awareness by keeping the driver's brain actively stimulated",
            "It drops the brain power dedicated to the immediate path of travel by 37%, and texting increases crash risk by 23 times",
            "It only affects manual reaction time but does not impact visual processing or cognitive focus",
            "The brain can successfully split focus with zero performance loss through subconscious multitasking"
        ],
        correctAnswer: 1,
        scenario: "A passenger claims that using a cell phone to text or navigate doesn't affect your ability to scan the road markings. What is the cognitive reality?",
        explanation: "Cognitive research shows that using a cell phone drops the brain power dedicated to driving by 37%. Texting while driving increases your overall crash risk by 23 times."
    },
    {
        id: "M9-021",
        moduleId: "module-9",
        category: "Alcohol",
        text: "How does alcohol physically affect a driver's visual systems and hazard-processing?",
        options: [
            "It sharpens detail clarity",
            "It relaxes the eye muscles, reducing coordinate focus and slowing down visual scanning and peripheral range",
            "It improves night vision",
            "It has no physical effect on eye speed"
        ],
        correctAnswer: 1,
        scenario: "**Correct Action**: **Alcohol directly relaxes the muscles controlling the eyes. This reduces visual coordination, slows visual scanning speeds, suppresses peripheral vision, and degrades depth perception.**",
        explanation: ""
    },
    {
        id: "M9-022",
        moduleId: "module-9",
        category: "Compliance",
        text: "Under Ohio's Implied Consent Law, what are you legally agreeing to by operating a vehicle on state roadways?",
        options: [
            "Allowing police searches of your home",
            "Submitting to blood, breath, or urine testing for alcohol or drugs if under suspicion of OVI",
            "Purchasing minimum comprehensive insurance coverage",
            "Retaking your driving test every year"
        ],
        correctAnswer: 1,
        scenario: "**Correct Action**: **Implied Consent means that by driving on public roads in Ohio, you automatically agree to submit to chemical tests (blood, breath, urine) for alcohol or drugs if stopped under suspicion of operating a vehicle impaired.**",
        explanation: ""
    },
    {
        id: "M9-023",
        moduleId: "module-9",
        category: "Drugs",
        text: "Which of the following is an accurate example of prescription or OTC drug synergy when mixed with alcohol?",
        options: [
            "The alcohol neutralizes the medication's side effects",
            "The sedative effects multiply exponentially, potentially suppressing the central nervous system to fatal levels",
            "The medication speeds up liver metabolism",
            "The body absorbs water faster"
        ],
        correctAnswer: 1,
        scenario: "**Correct Action**: **Synergy is the multiplicative effect of mixing alcohol with medications. Combining them can depress the central nervous system to highly dangerous, sometimes fatal levels, causing rapid blackouts.**",
        explanation: ""
    },
    {
        id: "M9-024",
        moduleId: "module-9",
        category: "Fatigue",
        text: "Which of the following is a critical warning sign that you are experiencing driver fatigue and must pull over to rest?",
        options: [
            "Frequent yawning, heavy eyelids, drifting lanes, and missing highway exits",
            "Having a fully charged phone battery",
            "Experiencing high visual acuity",
            "Taking a break every 100 miles"
        ],
        correctAnswer: 0,
        scenario: "**Correct Action**: **Warning signs of critical driver fatigue include frequent yawning, heavy, droopy eyelids, drifting out of your lane, tailgating, or missing your intended highway exits.**",
        explanation: ""
    },
    {
        id: "M9-025",
        moduleId: "module-9",
        category: "Psychological",
        text: "How should a driver handle a sudden surge of anger or stress to avoid an emotional hijack while behind the wheel?",
        options: [
            "Accelerating to clear traffic quickly",
            "Pulling over safely, performing a deep breathing reset, and refocusing on defensive driving safety",
            "Shouting at other drivers",
            "Tailgating to show frustration"
        ],
        correctAnswer: 1,
        scenario: "**Correct Action**: **If hijacked by sudden anger or stress, pull over safely in a secure parking spot. Execute a deep breathing exercise to lower your heart rate, calm your nervous system, and restore analytical, safe control.**",
        explanation: ""
    }
];
